<template>
  <div v-if="hasData" class="chart-list-wrapper">
    <b-card no-body class="h-100 p-2">
      <b-row no-gutters class="h-100">
        <b-col cols="8" class="pr-2 d-flex chart-col h-100">
          <e-chart
            :options="chartOptions"
            autoresize
            class="flex-fill h-100"
          ></e-chart>
        </b-col>
        <b-col cols="4" class="pl-2 d-flex flex-column list-col h-100">
          <b-list-group flush class="flex-fill overflow-auto">
            <b-list-group-item v-for="(item, index) in listData" :key="index">
              <div class="d-flex flex-wrap justify-content-between">
                <span class="font-weight-bold">{{ item.ip }}</span>
                <small class="text-muted">{{ formatTs(item.timestamp) }}</small>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-col>
      </b-row>
    </b-card>
  </div>
  <div v-else-if="loaded" class="ml-1">
    {{ $t("No Data Available") }}
  </div>
  <SpinnerCmpt v-else></SpinnerCmpt>
</template>

<script>
import moment from 'moment';

import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import XomesEventBus from '@/xvisor/bus/XomesEventBus';
import colorPalette from '@/xvisor/constants/colorPalette';
import colorPaletteShade from '@/xvisor/constants/colorPaletteShade';
import granularity from '@/xvisor/utilities/granularity';
import momentTimeFormat from '@/xvisor/constants/momentTimeFormat';
import themeStyle from '@/xvisor/utilities/themeStyle';
import timeFormat from '@/xvisor/utilities/timeFormat';

export default {
  components: {
    SpinnerCmpt,
  },
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      chartTitle: 'Activity',
      color: colorPaletteShade.green4,
      domain: '',
      listData: [],
      loaded: false,
      seriesData: [],
    };
  },
  computed: {
    chartOptions() {
      return {
        grid: {
          left: 50,
          right: 50,
          top: 50,
          bottom: 50,
        },
        series: this.formattedSeries,
        title: {
          text: this.domain,
          left: 'center',
          textStyle: {
            color: this.toggleColor,
          },
        },
        tooltip: {
          trigger: 'axis',
          formatter: (info) => `
            <div class="apache-echarts-tooltip">
              ${this.chartTitle} / ${moment(info[0].value[0]).format(this.timeSeriesFormat)}
              <br>
              ${info[0].marker} Connections: ${info[0].value[1]}
              <br>
            </div>
          `,
        },
        xAxis: [
          {
            type: 'time',
            boundaryGap: true,
            splitLine: {
              show: false,
            },
            axisTick: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLine: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLabel: {
              formatter: (value) => moment(value).format(this.timeSeriesFormat),
              fontSize: 9,
            },
          },
        ],
        yAxis: [
          {
            type: 'value',
            name: 'Activity',
            nameLocations: 'center',
            nameGap: 35,
            axisTick: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLine: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLabel: {
              fontSize: 9,
            },
          },
        ],
      };
    },
    formattedSeries() {
      const connections = this.seriesData.map((item) => [item.timestamp, item.count]);
      return {
        data: connections,
        name: this.domain,
        itemStyle: {
          color: this.color,
        },
        type: 'bar',
      };
    },
    hasData() {
      return this.loaded && this.seriesData.length > 0;
    },
    timestamps() {
      return this.seriesData.map((item) => item.timestamp);
    },
    timeSeriesFormat() {
      return timeFormat.momentFormat(this.timestamps, new Date());
    },
    toggleColor() {
      return themeStyle.styleToggle(colorPalette.black, colorPalette.white);
    },
  },
  methods: {
    formatTs(timestamp) {
      return moment(timestamp).format(momentTimeFormat.dateTime);
    },
    httpGet() {
      this.loaded = false;
      this.$http
        .get(this.url, {
          params: {
            start: this.timeRange.start.toISOString(),
            end: this.timeRange.end.toISOString(),
            stepMinutes: granularity.granularity24(this.timeRange),
            domain: this.domain,
          },
        })
        .then((response) => {
          const { connections, ips } = response.data;
          this.seriesData = connections;
          this.listData = ips;
        })
        .finally(() => {
          this.loaded = true;
        });
    },
  },
  created() {
    XomesEventBus.$on('update-selected-domain', (domain) => {
      this.domain = domain;
      this.httpGet();
    });
  },
  mounted() {
    if (this.domain === '') return;
    this.httpGet();
  },
  watch: {
    timeRange() {
      this.httpGet();
    },
    url() {
      this.httpGet();
    },
  },
};
</script>

<style scoped>
.chart-list-wrapper {
  height: 300px;
}

.chart-list-wrapper .h-100 {
  height: 100%;
}

.chart-col,
.list-col {
  min-height: 0;
}

.overflow-auto {
  overflow-y: auto;
}

</style>
